import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  FileText,
  Users,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Activity,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'GMUDs', href: '/gmuds', icon: FileText },
    { name: 'Aprovadores', href: '/aprovadores', icon: Users },
    { name: 'Relatórios', href: '/relatorios', icon: BarChart3 },
    { name: 'Auditoria', href: '/auditoria', icon: Activity },
    { name: 'Configurações', href: '/configuracoes', icon: Settings },
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Sidebar para desktop */}
      <div className='hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col'>
        <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 border-r border-gray-200'>
          <div className='flex h-16 shrink-0 items-center'>
            <h1 className='text-2xl font-bold text-primary-600'>GMUD Manager</h1>
          </div>
          <nav className='flex flex-1 flex-col'>
            <ul className='flex flex-1 flex-col gap-y-7'>
              <li>
                <ul className='-mx-2 space-y-1'>
                  {navigation.map(item => {
                    const Icon = item.icon;
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`
                            group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold
                            ${
                              isActive(item.href)
                                ? 'bg-primary-50 text-primary-600'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                            }
                          `}
                        >
                          <Icon className='h-6 w-6 shrink-0' />
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>

              {/* Seção do usuário */}
              <li className='mt-auto'>
                <div className='border-t border-gray-200 pt-4'>
                  <div className='flex items-center gap-x-3 px-2 py-2'>
                    {user?.photo ? (
                      <img className='h-8 w-8 rounded-full' src={user.photo} alt={user.name} />
                    ) : (
                      <User className='h-8 w-8 text-gray-400' />
                    )}
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm font-medium text-gray-900 truncate'>{user?.name}</p>
                      <p className='text-xs text-gray-500 truncate'>{user?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    className='group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-red-600 hover:bg-red-50'
                  >
                    <LogOut className='h-6 w-6 shrink-0' />
                    Sair
                  </button>
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Sidebar para mobile */}
      {sidebarOpen && (
        <div className='relative z-50 lg:hidden'>
          <div className='fixed inset-0 bg-gray-900/80' />
          <div className='fixed inset-0 flex'>
            <div className='relative mr-16 flex w-full max-w-xs flex-1'>
              <div className='absolute left-full top-0 flex w-16 justify-center pt-5'>
                <button
                  type='button'
                  className='-m-2.5 p-2.5'
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className='h-6 w-6 text-white' />
                </button>
              </div>
              <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4'>
                <div className='flex h-16 shrink-0 items-center'>
                  <h1 className='text-2xl font-bold text-primary-600'>GMUD Manager</h1>
                </div>
                <nav className='flex flex-1 flex-col'>
                  <ul className='flex flex-1 flex-col gap-y-7'>
                    <li>
                      <ul className='-mx-2 space-y-1'>
                        {navigation.map(item => {
                          const Icon = item.icon;
                          return (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                className={`
                                  group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold
                                  ${
                                    isActive(item.href)
                                      ? 'bg-primary-50 text-primary-600'
                                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                                  }
                                `}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <Icon className='h-6 w-6 shrink-0' />
                                {item.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </li>

                    {/* Seção do usuário mobile */}
                    <li className='mt-auto'>
                      <div className='border-t border-gray-200 pt-4'>
                        <div className='flex items-center gap-x-3 px-2 py-2'>
                          {user?.photo ? (
                            <img
                              className='h-8 w-8 rounded-full'
                              src={user.photo}
                              alt={user.name}
                            />
                          ) : (
                            <User className='h-8 w-8 text-gray-400' />
                          )}
                          <div className='flex-1 min-w-0'>
                            <p className='text-sm font-medium text-gray-900 truncate'>
                              {user?.name}
                            </p>
                            <p className='text-xs text-gray-500 truncate'>{user?.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            logout();
                            setSidebarOpen(false);
                          }}
                          className='group flex w-full gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 hover:text-red-600 hover:bg-red-50'
                        >
                          <LogOut className='h-6 w-6 shrink-0' />
                          Sair
                        </button>
                      </div>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo principal */}
      <div className='lg:pl-64'>
        {/* Header mobile */}
        <div className='sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden'>
          <button
            type='button'
            className='-m-2.5 p-2.5 text-gray-700 lg:hidden'
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className='h-6 w-6' />
          </button>
          <div className='flex-1 text-sm font-semibold leading-6 text-gray-900'>GMUD Manager</div>
        </div>

        {/* Conteúdo da página */}
        <main className='py-6'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
